// src/stores/data.js
import { defineStore } from 'pinia'
import { useAppStore } from './app'
import { useMapStore } from '@/stores/map'
import { IndexedDB } from '@/services/indexedDb'
import { getQueryStringParameter } from '@/utils/query'
import { filterRawDataForSelectedDate } from '@/utils/dataUtils'
import { getPoiIcon, getPoiName, getPoiDescription, getPoiIconUrl } from '@/utils/osmIcons'
import { encodePolyline, decodePolyline } from '@/utils/polyline'
import { calculateDistances, getDaysWithJourneys, getDayFromTimestamp, setSurveyAnswersFromSavedResponses, addSurveyToJourney, addSurveyToJourneyDay, extractJourneySurveyResponses, extractResponsesFromSurvey, evaluateJourneySurveyResponses, evaluateJourneyDaySurveyResponses, filterSurveyDefinitions, autoCompleteSurveyForDestination, clearSurveyAnswers } from '@/utils/journeyUtils'
import { users } from '@/utils/users'
import { h } from 'vue'
import { log } from '@/utils/logger'
import geoService from '@/services/geoService'
import dataService from '@/services/dataService'
import cloneDeep from 'lodash/cloneDeep'

const defaultProcess = {
	process: 'Venter på data',
	timestamp: Date.now(),
	duration: 0,
	progress: 0,
}

const defaultNewTrip = {
	index: 999,
	id: 999,
	locations: [{}, {}],
	activity_name: 'automotive',
	startTime: '12:00',
	origin: {},
	destination: {},
	endTime: '13:00',
	comment: '',
}

export const useDataStore = defineStore('data', {
	state: () => ({
		appInstallationId: '',
		surveyRedirectShortId: 'M72NGb',
		surveyRedirectId: '26602071-d079-4ffd-97bc-71e053072b9a',
		//surveyShortId: 'hlm6Hy',
		surveyShortId: '', //5pDE36,
		surveyId: '', // 87c9cc35-0676-4aff-8972-7aad924f2276
		surveyDefinitions: null,
		respondentName: '',
		respondentId: '',
		respondentAltId: '',
		respondentShortId: '',
		userCanNavigateTrips: true,
		userCanChangeDates: true,
		userCanUndo: false,
		userCanAccessDeveloperOptions: false,
		rawData: null,
		appData: null,
		appUserAgent: null,
		appState: null,
		processedData: null,
		tripsData: null,
		tripsDataUnfiltered: null,
		places: {},
		placesRaw: '',
		journeyDays: [],
		savedDays: [],
		savedDaysLookup: {},
		processing: false,
		processingError: false,
		loadingSurvey: false,
		surveyReady: false,
		currentProcess: defaultProcess,
		processHistory: [],
		availableDates: {},
		originalJourneyDays: [],
		currentJourneyIndex: 0,
		currentJourneyClone: null,
		//hasData: false,
		id: getQueryStringParameter('id') ?? '',
		dateFrom: getQueryStringParameter('from') ?? '',
		dateTo: getQueryStringParameter('to') ?? '',
		isApp: getQueryStringParameter('app') ? true : false,
		platform: getQueryStringParameter('platform') || 'web',
		buildVersion: (() => {
			const parts = (getQueryStringParameter('version') || '1.0.10').split('.')
			if (parts.length < 2) return 1.0
			if (parts.length < 3) return parseFloat(parts[0] + '.' + parts[1])
			return parseFloat(parts[0] + '.' + parts[1] + parts[2])
		})(),
		disableDiary: getQueryStringParameter('diary') === 'false' ? true : false,
		disableSaving: getQueryStringParameter('save') === 'false' ? true : false,
		altid: getQueryStringParameter('altid') ?? '',
		todayDate: new Date(),
		selectedDate: null,
		selectedDateIsToday: false,
		selectedTripId: '',
		transportModes: [
			{ mode: 'automotive', type: 'automotive', name: 'Bil', desc: 'Biltur', color: '#708090' },
			{ mode: 'bus', type: 'bus', name: 'Buss', desc: 'Busstur', color: '#76a300' },
			{ mode: 'tram', type: 'tram', name: 'Trikk', desc: 'Trikketur', color: '#0b91f0' },
			{ mode: 'metro', type: 'metro', name: 'T-bane', desc: 'T-banetur', color: '#ec700c' },
			{ mode: 'train', type: 'train', name: 'Tog', desc: 'Togtur', color: '#191970' },
			{ mode: 'walking', type: 'walking', name: 'Til fots', desc: 'Til fots', color: '#ffc107' },
			{ mode: 'cycling', type: 'cycling', name: 'Sykkel', desc: 'Sykkeltur', color: '#FF1493' },
			{ mode: 'mc', type: 'mc', name: 'MC', desc: 'MC', color: '#000000' },
			{ mode: 'micro', type: 'micro', name: 'Mikromobilitet', desc: 'Mikromobilitet', color: '#008080' },
			{ mode: 'water', type: 'water', name: 'Rutebåt', desc: 'Båttur', color: '#682b88' },
			{ mode: 'airtravel', type: 'airtravel', name: 'Fly', desc: 'Flytur', color: '#E5212D' },
			{ mode: 'other', type: 'other', name: 'Annet', desc: 'Annet', color: '#333333' },
			// { mode: 'stationary', type: 'stationary', name: 'Stille', desc: 'Stille', color: '#aaaaaa' },
		],
		purposeTypes: [
			{ id: 1, name: 'Arbeid', description: 'Reise fra eller til arbeid/skole/studiested' },
			{ id: 2, name: 'Arbeidsreise', description: 'Reise eller møte i tilknytning til arbeidet' },
			{ id: 3, name: 'Fulgte barn', description: 'Fulgte barn eller andre til forskjellige aktiviteter' },
			{ id: 4, name: 'Gjorde innkjøp', description: 'Dagligvarer eller andre artikler til hjemmet' },
			{ id: 5, name: 'Ærend', description: 'Bilverksted, bank, post, offentlig etat etc.' },
			{ id: 6, name: 'Underholdning', description: 'Var på kino, idrettsarrangement, kafé etc.' },
			{ id: 7, name: 'Besøk', description: 'Besøkte slekt eller venner' },
			{ id: 8, name: 'Helsetjeneste', description: 'Legebesøk eller andre medisinske tjenester' },
			{ id: 9, name: 'Organiserte aktivitet', description: 'Deltakelse i idrettsaktiviteter, musikkaktiviteter, kulturalaktiviteter etc.' },
			{ id: 10, name: 'Fritidsaktivitet', description: 'Gåtur eller annen fritidsaktivitet inkl. ferieaktiviteter' },
			{ id: 97, name: 'Hjem fra aktivitet', description: 'Reiste hjem fra en aktivitet' },
			{ id: 98, name: 'Annet', description: 'Andre typer reiser' },
		],
		mockUserId: '1234',
		mockAudience: {
			alt_id: '1234',
			short_id: '1234',
			resp_id: '1234',
			status: 'COMPLETED',
			name: 'Test',
			survey_id: 'mock_survey_id',
			survey_short_id: 'mock_survey_short_id',
		},
		mockSurveyDefinitions: {
			journey: [{}],
			trip: [{}],
			places: [
				{
					id: 'BOSTED_HOVED',
					type: 'home',
					address: 'Veslejordet 14, Fjerdingby, Norge',
					latitude: 59.90542310000001,
					longitude: 11.0992703,
					place_id: 'ChIJvZQDDuBiQUYRimYSmhTOXNY',
				},
				{
					id: 'OPPMOTE_ARBEID',
					type: 'work',
					address: 'Rebel, Universitetsgata, Oslo, Norge',
					latitude: 59.9173759,
					longitude: 10.7401181,
					place_id: 'ChIJ10INoKFvQUYRUoOISMdTd1o',
				},
			],
		},
		currentSplitLocationIndex: -1,
		currentSplitLocationRangeIndex: -1,
		currentSplitLocation: null,
		currentTrip: null,
		currentTripIndex: -1,
		currentEditTrip: null,
		currentEditOriginal: null,
		currentLocationType: 'origin',
		showGeocoder: false,
		showEditTripDialog: false,
		showLoginDialog: false,
		installDate: new Date(),
		pauseDate: new Date(),
		isFrame: document.location.search.includes('iframe'),
		deeplink: '',
		surveyStartedDate: '',
		surveyCompletedDate: '',
		permissionStartedDate: '',
		permissionCompletedDate: '',
		deeplinkIdentify: '',
		loadingAudience: false,
		lastLocation: null,
		hasApprovedJourney: false,
		defaultLocation: { latitude: 59.9139, longitude: 10.7522, zoom: 8 },
	}),

	getters: {
		isPreview: (state) => {
			if (document.location.search.includes('preview')) return true
			if (state.respondentAltId == state.mockUserId) return true // For testing purposes
			return false
		},
		justInstalled: (state) => {
			const yesterDay = new Date()
			yesterDay.setDate(yesterDay.getDate() - 1)
			return state.installDate.toShortDateString() == new Date().toShortDateString() || state.installDate.toShortDateString() == yesterDay.toShortDateString()
		},
		yesterDate: (state) => {
			var yesterDay = new Date()
			yesterDay.setDate(yesterDay.getDate() - 1)
			yesterDay.setHours(12, 0, 0, 0)
			return yesterDay
		},
		todaySelected: (state) => {
			if (state.selectedDate == null) return false
			return state.selectedDate.toShortDateString() == state.todayDate.toShortDateString()
		},
		yesterDaySelected: (state) => {
			if (state.selectedDate == null) return false
			return state.selectedDate.toShortDateString() == state.yesterDate.toShortDateString()
		},
		yesterDayReady: (state) => {
			return state.todayDate.getHours() >= 12
		},
		defaultNewTrip: () => JSON.parse(JSON.stringify(defaultNewTrip)),
		hasAvailableDates: (state) => Object.keys(state.availableDates).length > 0,
		currentTripsNotDeleted: (state) => state.currentJourney?.trips.filter((trip) => !trip.deleted) || [],
		currentDayIndex: (state) => {
			if (state.journeyDays.length > 0) {
				if (state.selectedDate) {
					const selectedDateStr = state.selectedDate.toShortDateString()
					const index = state.journeyDays.findIndex((day) => day.date == selectedDateStr)
					if (index > -1) return index
				}
			}
			return -1
		},
		hasDayReady: (state) => {
			if (state.selectedDate == null || state.todaySelected || state.justInstalled) return false
			if (state.yesterDaySelected && !state.yesterDayReady) return false
			return true
		},
		hasData: (state) => {
			return state.currentJourneys.length > 0
		},
		currentJourneyDay: (state) => {
			if (state.currentDayIndex < 0) return null
			return state.journeyDays[state.currentDayIndex] || null
		},
		currentJourney: (state) => {
			if (state.currentJourneyDay == null) return null
			return state.currentJourneyDay.journeys[state.currentJourneyIndex] || null
		},
		currentFirstTrip: (state) => {
			if (state.currentJourney == null) return null
			return state.currentJourney.trips[0] || null
		},
		currentLastTrip: (state) => {
			if (state.currentJourney == null) return null
			return state.currentJourney.trips[state.currentJourney.trips.length - 1] || null
		},
		currentJourneys: (state) => {
			if (state.currentJourneyDay == null) return []
			return state.currentJourneyDay.journeys || []
		},
		currentSplitTripIndex: (state) => {
			const trips = state.journeyDays[state.currentDayIndex]?.journeys[state.currentJourneyIndex]?.trips || []
			return trips.findIndex((trip) => trip.split)
		},
		currentSplitTrip: (state) => {
			const trips = state.journeyDays[state.currentDayIndex]?.journeys[state.currentJourneyIndex]?.trips || []
			const index = trips.findIndex((trip) => trip.split)
			return index > -1 ? trips[index] : null
		},
		selectedDateFormatted_WWWddMMM: (state) => {
			if (!state.selectedDate) return ''
			return state.selectedDate.toLocaleDateString('no', { weekday: 'long', day: 'numeric', month: 'long' })
		},
		currentDestinationPois: (state) => {
			console.warn('data: currentDestinationPois')
			if (!state.currentLastTrip?.destination_pois) return []

			let places = JSON.parse(JSON.stringify(state.currentLastTrip.destination_pois))
			let sortedPlaces = places.sort((a, b) => (a.distance > b.distance ? 1 : -1))

			// Add default options at start
			sortedPlaces.unshift({ type: 'place:other', name: 'Annet', location: state.currentLastTrip.destination })
			sortedPlaces.unshift({ type: 'place:work', name: 'Jobb', location: window.etl.work?.center })
			sortedPlaces.unshift({ type: 'place:home', name: 'Hjem', location: window.etl.home?.center })

			// Add icons and format data
			sortedPlaces.forEach((place) => {
				place.icon = getPoiIcon(place)
				place.iconUrl = getPoiIconUrl(place)
				place.title = getPoiName(place)
				place.description = getPoiDescription(place)
			})

			return sortedPlaces
		},
	},

	actions: {
		async loadStates() {
			setLoad('data.js:loadStates', 7)
			log(`dataStore: loadStates`).white()

			// Load certain saved values
			await this.loadSavedStates()

			// Load saved survey id async
			await this.loadRespondentData()

			// Make sure survey definitions are loaded
			this.loadSurveyDefinitions()

			// No need to wait for these
			kettle.get('userCanNavigateTrips').then(async (value) => {
				if (value) this.userCanNavigateTrips = value == 'true' ? true : false
			})
			kettle.get('userCanChangeDates').then(async (value) => {
				if (value) this.userCanChangeDates = value == 'true' ? true : false
			})
			kettle.get('userCanUndo').then(async (value) => {
				if (value) this.userCanUndo = value == 'true' ? true : false
			})

			if (window.self !== window.top) {
				var self = this
				setTimeout(function () {
					window.parent.updateFeatures({
						userCanNavigateTrips: self.userCanNavigateTrips,
						userCanChangeDates: self.userCanChangeDates,
						userCanUndo: self.userCanUndo,
					})
				}, 100)
			}
		},
		async loadRespondentData() {
			try {
				setLoad('data.js:loadRespondentData', 11)
				const [surveyId, surveyShortId, respondentShortId, respondenName, respondentId, respondentAltId] = await Promise.all([kettle.get('survey_id'), kettle.get('survey_short_id'), kettle.get('respondent_short_id'), kettle.get('respondent_name'), kettle.get('respondent_id'), kettle.get('respondent_alt_id')])
				this.surveyId = surveyId
				this.surveyShortId = surveyShortId
				this.respondentName = respondenName || 'Anonym'
				this.respondentShortId = respondentShortId
				this.respondentId = respondentId
				this.respondentAltId = respondentAltId
				setLoad('data.js:loadRespondentData', 12)
				// All values are now loaded - continue with your logic here
			} catch (error) {
				setLoad('data.js:loadRespondentData', 12, error)
				console.error('Error loading data:', error)
			}
		},
		async loadSavedStates() {
			setLoad('data.js:loadSavedStates', 8)
			this.todayDate = new Date()
			const installDateString = await kettle.get('install_date')
			if (installDateString) {
				this.installDate = new Date(installDateString)
			} else {
				this.installDate = new Date()
				await kettle.set('install_date', this.installDate.toISOString())
			}

			this.appInstallationId = await kettle.get('appInstallationId')
			this.surveyStartedDate = await kettle.get('survey_started_date')
			this.surveyCompletedDate = await kettle.get('survey_completed_date')
			this.permissionStartedDate = await kettle.get('permission_started_date')
			this.permissionCompletedDate = await kettle.get('permission_completed_date')
			this.journeyApprovedValue = await kettle.get('journey_approved')

			// TODO: This is disabled for iOS until the app has been updated with a new version of kettle SDK.
			setLoad('data.js:loadSavedStates', 9)
			var placesData = ''
			try {
				if (this.buildVersion >= 1.011) {
					placesData = await kettle.get('places')
					this.placesRaw = placesData || ''
					this.places = placesData ? JSON.parse(placesData) : {}
				}
			} catch (error) {
				console.warn('Failed to parse places data:', error, placesData)
				this.places = {}
			}
			setLoad('data.js:loadSavedStates', 10)
		},
		async getAudience(altId = this.respondentAltId) {
			console.warn('data: getAudience', altId)
			// For testing purposes, return a mock audience
			if (altId == '1234') {
				this.installDate = new Date(2025, 1, 1)
				kettle.set('install_date', this.installDate.toISOString())
				return this.mockAudience
			}

			const appStore = useAppStore()
			this.loadingAudience = true
			try {
				const result = await dataService.getAudience(altId)
				this.loadingAudience = false
				appStore.events.trackLoginSuccess()
				return result
			} catch (error) {
				console.error('Failed to get audience data:', error)
				this.loadingAudience = false
				this.clearUser()
				appStore.events.trackLoginFailure()
				return {}
			}
		},
		async loadSurveyDefinitions() {
			setLoad('data.js:loadSurveyDefinitions', 13)
			if (this.respondentId == this.mockUserId) {
				console.warn('data: loadSurveyDefinitions - using mock data for testing')
				const response = await fetch('/preview/survey.json')
				const jsonString = await response.text()
				this.surveyDefinitions = JSON.parse(jsonString)
				return
			}
			if (this.respondentId) {
				// Get survey definitions - cache is returned async if available and updated data in callback
				const originalSurveyDefinitions = await dataService.getSurveyDefinitions(this.surveyId, this.respondentId, true, (updatedSurveyDefinitions) => {
					setLoad('data.js:getSurveyDefinitions callback', 15)
					// Removes irrelevant survey questions
					filterSurveyDefinitions(updatedSurveyDefinitions)
					this.surveyDefinitions = updatedSurveyDefinitions
				})
				setLoad('data.js:loadSurveyDefinitions', 14)
				// Removes irrelevant survey questions
				filterSurveyDefinitions(originalSurveyDefinitions)
				this.surveyDefinitions = originalSurveyDefinitions
			}
		},
		async deleteSurvey() {
			await dataService.resetSurvey(this.surveyId, this.respondentId)
		},
		addSurveyToCurrentJourneyDay() {
			if (this.currentJourneyDay) {
				addSurveyToJourneyDay(this.currentJourneyDay, this.surveyDefinitions)
			}
		},
		async addSurveyToJourneys() {
			// Load survey definitions if not already loaded
			if (!this.surveyDefinitions) await this.loadSurveyDefinitions()
			// Iterate each day and addSurveyToJourney for each journey
			if (this.surveyDefinitions && this.surveyDefinitions.trip) {
				this.journeyDays.forEach((day) => {
					day.journeys.forEach((journey, index) => {
						addSurveyToJourney(journey, index, this.surveyDefinitions)
					})
					addSurveyToJourneyDay(day, this.surveyDefinitions)
				})
			} else {
				console.error('addSurveyToJourneys: No surveyDefinitions found')
			}
			// TODO: Check surveyResponses and add answers back to surveys
			this.journeyDays.forEach((day) => {
				day.journeys.forEach((journey, index) => {
					// Set answers for all journeys from survey responses
					setSurveyAnswersFromSavedResponses(journey.survey, journey.surveyResponses)
					// Set answers for all trips from survey responses
					journey.trips.forEach((trip) => {
						setSurveyAnswersFromSavedResponses(trip.survey, trip.surveyResponses)
					})
				})
				if (day.survey) {
					day.survey.forEach((survey, index) => {
						// Set answers for all day survey responses
						setSurveyAnswersFromSavedResponses(survey, day.surveyResponses)
					})
				}
			})
		},
		resetSurveysFromJourneyIndex(index) {
			// Reset survey for all journeys after index
			for (let i = index; i < this.currentJourneyDay.journeys.length; i++) {
				let journey = this.currentJourneyDay.journeys[i]
				addSurveyToJourney(journey, i, this.surveyDefinitions)
			}
			// Re-evaluate current journey survey
			this.evaluateCurrentJourneySurveyResponses()
		},
		addSurveyToCurrentJourney() {
			if (this.currentJourney) {
				addSurveyToJourney(this.currentJourney, this.currentJourneyIndex, this.surveyDefinitions)

				// Re-evaluate current journey survey
				this.evaluateCurrentJourneySurveyResponses()
			}
		},
		async autoCompleteSurveyForJourneyDestination(journey = this.currentJourney) {
			if (journey && journey.survey) {
				console.warn('autoCompleteSurveyForJourneyDestination', journey)
				journey.survey.forEach((surveyQuestion) => {
					console.warn('autoCompleteSurveyForDestination', surveyQuestion.id, journey.destination, 'source', 'dataStore')
					autoCompleteSurveyForDestination(surveyQuestion, journey.destination, 'dataStore')
				})
			}
		},
		async evaluateCurrentJourneySurveyResponses(journey = this.currentJourney) {
			if (journey) {
				console.warn('evaluateCurrentJourneySurveyResponses', journey)
				evaluateJourneySurveyResponses(journey)
				journey.surveyResponses = extractJourneySurveyResponses(journey)
			}
		},
		evaluateCurrentJourneyDaySurveyResponses() {
			if (this.currentJourneys && this.currentJourneys.length > 0) {
				// Evaluate survey responses for current journey day, all its journeys and all its trips
				evaluateJourneyDaySurveyResponses(this.currentJourneyDay)

				var allResponses = {}

				// Extract and merge responses from all completed journeys in currentJourneys
				this.currentJourneys.forEach((journey) => {
					if (journey.surveyJourneyCompleted) {
						journey.surveyResponses = extractJourneySurveyResponses(journey)
						if (journey.surveyResponses) {
							allResponses = { ...allResponses, ...journey.surveyResponses }
						}
					}
				})

				// Merge responses from current journey day surveys
				if (this.currentJourneyDay && this.currentJourneyDay.survey) {
					this.currentJourneyDay.survey.forEach((survey) => {
						var responses = extractResponsesFromSurvey(survey)
						allResponses = { ...allResponses, ...responses }
					})
				}

				this.currentJourneyDay.surveyResponses = allResponses
				console.warn('evaluateCurrentJourneyDaySurveyResponses', this.currentJourneyDay.surveyResponses)
			}
		},
		async initRespondent(altId = this.respondentAltId) {
			const appStore = useAppStore()
			console.warn('data: initRespondent', 'respondentAltId', this.respondentAltId, 'respondentId', this.respondentId, 'respondentShortId', this.respondentShortId, 'surveyId', this.surveyId, 'surveyShortId', this.surveyShortId)
			if (this.respondentId && this.respondentShortId && this.respondentAltId && this.surveyId && this.surveyShortId && appStore.surveyCompleted && appStore.onboardedSurvey) return
			const audienceData = await this.getAudience(altId)
			if (audienceData.resp_id) {
				console.warn('initRespondent', audienceData)
				this.surveyShortId = audienceData.survey_short_id
				this.surveyId = audienceData.survey_id
				this.respondentShortId = audienceData.short_id
				this.respondentId = audienceData.resp_id
				this.respondentAltId = audienceData.alt_id
				this.respondentName = audienceData.name || 'Anonym'
				kettle.set('respondent_short_id', this.respondentShortId)
				kettle.set('respondent_id', this.respondentId)
				kettle.set('respondent_name', this.respondentName)
				kettle.set('respondent_alt_id', this.respondentAltId)
				kettle.set('survey_id', this.surveyId)
				kettle.set('survey_short_id', this.surveyShortId)
				if (!appStore.surveyCompleted && audienceData.status == 'COMPLETED') {
					kettle.set('onboarded_survey', 'true')
					kettle.set('survey_completed', 'true')
					appStore.surveyCompleted = true
					appStore.onboardedSurvey = true
					// Init again if it turns out the survey is completed
					appStore.initApp('initRespondent')
				}
			}
		},
		async handleDeeplink(url) {
			if (!url) {
				console.warn('handleDeeplink called with an empty URL.')
				return
			}

			const appStore = useAppStore()

			// Keep the original URL for logging if needed
			this.deeplink = url
			console.log('Handling deep link:', url)
			let altId = null

			try {
				const parsedUrl = new URL(url)

				// Case 1: Handle the custom URL scheme (reisevaner://identify/1234)
				if (parsedUrl.protocol === 'reisevaner:') {
					if (parsedUrl.hostname === 'identify' && parsedUrl.pathname) {
						// The pathname will be '/1234', so we remove the leading '/'
						altId = parsedUrl.pathname.substring(1)
					}
				}
				// Case 2: Handle the HTTPS Universal Link (https://.../?id=1234)
				else if (parsedUrl.protocol === 'https:') {
					// Use URLSearchParams to easily get the 'id' parameter
					altId = parsedUrl.searchParams.get('id')
				}

				// --- Common logic after extracting the ID ---
				if (altId) {
					console.log('Successfully extracted altId:', altId)
					// This is your existing logic to trigger the login flow
					if (!this.respondentAltId || !this.respondentId || !this.respondentShortId) {
						this.showLoginDialog = true
						this.respondentAltId = altId
					}
				}

				// Deeplink to specific page
				if (url.indexOf('/page') > -1) {
					// Get page param after page/
					const pageName = url.split('page/')[1]
					if (pageName) {
						console.warn('Deep Link: Navigating to page', pageName)
						if (pageName == 'profile') {
							appStore.expandedProfile = true
						} else {
							if (pageName == 'permissions' || pageName == 'intro') {
								appStore.setPage(pageName)
							}
						}
					}
				}

				// Deeplink just to refresh states
				if (url.indexOf('/refresh') > -1) {
					this.loadSavedStates()
					await appStore.getPermissions()
					await appStore.getConsents()
					appStore.initApp('resume')
				}
			} catch (error) {
				console.error('Deep Link Error: The provided URL is invalid.', url, error)
			}
		},
		setCurrentJourney(journey) {
			this.journeyDays[this.currentDayIndex].journeys[this.currentJourneyIndex] = journey
		},
		async loadAndProcessData(clearData = false) {
			console.warn('data: loadAndProcessData')
			setLoad('data.js:loadAndProcessData', 17)
			if (clearData) {
				this.tripsData = null
				this.journeyDays = []
				this.processing = true //this.respondentId != this.mockUserId // Don't show processing for mock user
				this.currentProcess = defaultProcess
				this.processHistory = []
				this.rawData = null

				// Clear etl process history
				if (window.etl) window.etl.process_history = []
			}
			if (this.isApp && !this.isPreview) {
				await this.loadAndProcessAppData()
			} else {
				await this.loadAndProcessCloudData()
			}
		},
		async getUserLocation() {
			const appStore = useAppStore()
			// Immediately return default if permission is not granted.
			if (!appStore.permissions.location_wheninuse) {
				this.lastLocation = this.defaultLocation
				return this.lastLocation
			} else {
				return await kettle.getLocation()
			}
		},
		async flyToUserLocation(zoom) {
			const userLocation = await this.getUserLocation()
			const mapStore = useMapStore()
			if (userLocation) mapStore.flyToLocation(userLocation, zoom, 3000)
		},
		async loadAndProcessAppData() {
			var isFirstGet = false
			if (this.selectedDate == null) {
				this.selectedDate = new Date()
				isFirstGet = true
			}
			var startDate = new Date(this.selectedDate)
			var dateLongoAgo = new Date(this.selectedDate)
			var endDate = new Date(this.selectedDate)
			startDate.setDate(startDate.getDate() - 1)
			startDate.setHours(0, 0, 0, 0)
			dateLongoAgo.setDate(startDate.getDate() - 90)
			endDate.setDate(endDate.getDate() + 1)
			endDate.setHours(4, 0, 0, 0)
			var startDateIso = startDate.toISOString()
			var endDateIso = endDate.toISOString()
			var dateLongoAgoIso = dateLongoAgo.toISOString()
			let dataRequests = [
				{ type: 'custom', key: 'trips', startDate: dateLongoAgoIso, endDate: endDateIso },
				{ type: 'beacons', startDate: startDateIso, endDate: endDateIso },
				{ type: 'activities', startDate: startDateIso, endDate: endDateIso },
				{ type: 'locations', startDate: startDateIso, endDate: endDateIso },
				{ type: 'locationDays', startDate: dateLongoAgoIso, endDate: endDateIso },
				{ type: 'visits', startDate: dateLongoAgoIso, endDate: endDateIso },
			]
			console.warn('kettle.getData', dataRequests)
			let jsonData = await kettle.getData(dataRequests, { compress: true, parse: false })

			this.rawData = JSON.parse(jsonData)

			// Set userAgent for the app
			this.appUserAgent = this.rawData?.userAgent || {}
			this.appState = this.rawData?.state || {}

			// If selectedDate has not been selected by user the use the last date with data
			if (isFirstGet && this.rawData.locationDays) {
				// Use yesterday as default date

				console.warn('data: kettle.getData isFirstGet', isFirstGet, this.rawData.locationDays)
				this.selectedDate = this.yesterDate

				// Check if yesterday is present in locationDays
				var yesterDayIsoDate = this.selectedDate.toShortDateString()
				if (!this.rawData.locationDays[yesterDayIsoDate]) {
					console.error('data: kettle.getData yesterday not found in locationDays', this.yesterDate.toISOString())
				}
			}

			console.timeEnd('kettle.getData finished')
			console.warn('data: kettle.getData', dataRequests, this.rawData)
			await this.processData(jsonData)
		},
		async loadAndProcessCloudData() {
			console.warn('data: loadAndProcessCloudData', this.respondentAltId)

			const urlParams = new URLSearchParams(window.location.search)

			// When logged in as mock user, use mockUserId
			if (this.respondentAltId == this.mockUserId || this.isPreview) {
				this.id = this.mockUserId
				this.dateFrom = '2024-12-02'
				this.dateTo = '2024-12-08'
			} else {
				if (!this.id || !this.dateFrom || !this.dateTo) return
				if (users[this.id]) this.id = users[this.id]
			}

			console.warn('data: params', this.id, this.dateFrom, this.dateTo)
			console.warn('data: id', this.id)

			if (this.rawData == null) {
				console.warn('data: rawData is null')
				const urlData = `${this.id}-${this.dateFrom}-${this.dateTo}`
				const cachedUrlData = await IndexedDB.getItem('urlData')

				console.warn('data: selectedDate', this.selectedDate)

				// Use json file if isPreview is true
				if (this.isPreview) {
					this.selectedDate = new Date(2024, 11, 4)
					const response = await fetch(`/preview/${this.id}.json`)
					const jsonString = await response.text()
					let success = await this.processData(jsonString)
					if (success) {
						this.rawData = jsonString
						console.error('data: Using preview .json data', this.rawData.length)
					} else {
						console.error('data: Failed to process preview .json data')
					}
					return
				}

				// Check if we can use cached data
				if (urlData === cachedUrlData) {
					const cachedData = await IndexedDB.getItem('jsonData')
					if (cachedData) {
						this.appData = JSON.parse(cachedData)
						this.appUserAgent = this.appData.userAgent
						this.appState = this.appData.state || {}
						console.error('data: Using cached data', this.appData)
						let success = await this.processData(cachedData)
						return
					}
				}

				// Load new data
				const jsonData = await dataService.getAppData(this.id, this.dateFrom, this.dateTo)

				// Cache the new data
				await IndexedDB.setItem('jsonData', jsonData)
				await IndexedDB.setItem('urlData', urlData)
				this.rawData = jsonData

				// Parse the JSON data
				this.appData = JSON.parse(jsonData)
				this.appUserAgent = this.appData.userAgent
				this.appState = this.appData.state || {}

				console.warn('data: jsonData', jsonData.length)
			}

			// Process the data
			await this.processData(this.rawData)
		},

		async processData(jsonData) {
			this.processingError = false
			console.warn('data: processData', jsonData.length)
			if (Etl) {
				// Similate the expected data from iOS and Android app
				if (!this.isApp || this.isPreview) {
					var replaceAppId = ''

					var filteredData = filterRawDataForSelectedDate(jsonData, this.selectedDate, this.dateTo, replaceAppId)

					if (!filteredData) {
						console.error('data: processData filteredData is null')
						return false
					}

					// TODO: Temporary for web preview
					if (this.isPreview && dataStore.respondentAltId) {
						filteredData.appInstallationId = dataStore.respondentAltId
					}

					jsonData = JSON.stringify(filteredData)
					if (this.selectedDate == null) {
						this.selectedDate = filteredData.selectedDate
					}
				}

				if (!window.etl) {
					window.etl = new Etl(false)
					window.etl.process_callback = this.registerProcess
					window.etl.debug = false
				}

				const self = this

				try {
					var tripsJson = await window.etl.processDataForApp(jsonData, async function (appInstallationId) {
						console.warn('processDataForApp AppId', appInstallationId)
						self.appInstallationId = appInstallationId
						kettle.set('appInstallationId', appInstallationId)
						await self.getSavedDays('processData')
						console.warn('data: savedDays', self.savedDays)
					})
				} catch (error) {
					console.warn('data: processDataForApp error', error)
					this.processing = false
					const appStore = useAppStore()
					appStore.setPage('onboarding')
					appStore.loaded = true
					this.processingError = true
				}

				return true
			} else {
				console.log('data: ETL Class not found')
			}
		},

		// async setTripsData(normalizedTrips, rawTrips, days, locations, activities, transports, useActivityData, scriptVersion, configScriptVersion) {
		// 	console.warn('data: setTripsData normalizedTrips', normalizedTrips)
		// 	this.tripsData = normalizedTrips
		// 	this.processTripsData('setTripsData')
		// },

		setAvailableDatesFromETL() {
			Object.keys(window.etl.data.locationDays).forEach((dateStr) => {
				const dateKey = new Date(dateStr).toShortDateString()
				if (dateKey != this.todayDate.toShortDateString()) {
					// Skip the last date if we are in web preview mode
					if (!this.isApp && dateKey == this.dateTo) return
					this.availableDates[dateKey] = window.etl.data.locationDays[dateStr] > 5
				}
			})
		},

		processTripsData(source) {
			console.warn('data: processTripsData', source, this.tripsData)
			if (!this.tripsData) return

			this.journeyDays = getDaysWithJourneys(this.tripsData, this.surveyDefinitions)
			this.originalJourneyDays = JSON.parse(JSON.stringify(this.journeyDays))
			this.addSurveyToJourneys()

			// Set the available dates object from etl data
			this.setAvailableDatesFromETL()

			// If we have saved days, merge the data with the current journey days
			if (this.savedDays) {
				this.mergeSavedDays()
			}

			if (this.journeyDays.length > 0) {
				this.initDay()

				// Set app state and page
				const appStore = useAppStore()
				appStore.loaded = true
				appStore.loading = false
				if (appStore.currentPage != 'trips') {
					console.warn('data: processTripsData currentJourneyDay', this.currentJourneyDay?.approved)
					if (this.currentJourneyDay && this.currentJourneyDay.approved) {
						appStore.setPage('approved')
					} else {
						appStore.setPage('intro')
					}
				}
				// If bottomSheet is closed then open it
				if (appStore.bottomSheet.currentHeight < appStore.bottomSheet.BottomSheetStates.SMALL) {
					appStore.bottomSheet.setHeightNormal(500, 'DataStore - processTripsData', true)
				}
			}
		},

		initDay() {
			console.warn('data: initDay')
			const mapStore = useMapStore()
			if (this.currentJourney?.trips?.length) {
				mapStore.plotTrips()
				mapStore.plotHomeWork(window.etl.home, window.etl.work)
			}
		},

		createEmptyJourneyDay(stationaryDay = false) {
			this.loadSurveyDefinitions()
			this.journeyDays = [
				{
					date: this.selectedDate.toShortDateString(),
					journeys: [],
				},
			]
			this.addJourney(0, stationaryDay)
		},

		// setJourneyDefaults() {
		// 	if (!this.currentJourney) return

		// 	// Set defaults for work journey
		// 	if (this.currentLastTrip?.to_work) {
		// 		this.currentJourney.selectedPoi = this.currentDestinationPois[1] // Work POI
		// 		this.currentJourney.selectedPurpose = this.purposeTypes[0] // Work purpose
		// 	}

		// 	// Set defaults for home journey
		// 	if (this.currentLastTrip?.to_home) {
		// 		this.currentJourney.selectedPoi = this.currentDestinationPois[0] // Home POI
		// 		this.currentJourney.selectedPurpose = this.purposeTypes[0] // Work purpose
		// 	}
		// },

		getTransportMode(mode) {
			return this.transportModes.find((m) => m.mode === mode || m.type === mode) || {}
		},

		formatDistance(distanceMeters) {
			return `${Math.round(distanceMeters / 100) / 10}km`
		},

		formatTime(timestamp) {
			const date = new Date(timestamp)
			return `${('0' + date.getHours()).slice(-2)}:${('0' + date.getMinutes()).slice(-2)}`
		},

		formatDurationFromSeconds(seconds) {
			const hours = Math.floor(seconds / 3600)
			const minutes = Math.floor((seconds % 3600) / 60)
			const remainingSeconds = Math.floor(seconds % 60)
			let result = []
			if (hours) result.push(hours + 'h')
			if (minutes) result.push(minutes + 'min')
			if (!hours && minutes < 5 && remainingSeconds) result.push(remainingSeconds + 's')
			if (seconds < 60) return seconds + 's'
			return result.join(' ')
		},

		formatDuration(startStr, endStr) {
			const startDate = new Date(startStr)
			const endDate = new Date(endStr)
			const duration = (endDate.getTime() - startDate.getTime()) / 1000 // convert milliseconds to seconds
			const hours = Math.floor(duration / 3600)
			const minutes = Math.floor((duration % 3600) / 60)
			const seconds = Math.floor(duration % 60)
			return `${hours}h ${minutes}m ${seconds}s`
		},

		updateData(data) {
			console.log('updateData', data)
		},

		// Only return trips that starts after 04:00 and start before 04:00 the next day
		filterTripsForSelectedDate(trips) {
			const startDate = new Date(this.selectedDate)
			const endDate = new Date(this.selectedDate)
			startDate.setHours(4, 0, 0, 0)
			endDate.setDate(endDate.getDate() + 1)
			endDate.setHours(4, 0, 0, 0)
			const startTimestamp = startDate.getTime()
			const endTimestamp = endDate.getTime()
			return trips.filter((trip) => {
				return trip.departure_timestamp >= startTimestamp && trip.departure_timestamp <= endTimestamp
			})
		},

		async registerProcess(process_entry, process_history, process_result) {
			this.currentProcess = process_entry
			const duration = this.processHistory.length ? process_entry.timestamp - this.processHistory[this.processHistory.length - 1].timestamp : 0
			process_entry.duration = duration.toFixed(0)

			// In the last process update process_result will have the finished trips data
			if (process_entry.completed) {
				console.warn('data: registerProcessCompleted', process_entry, process_result)
				if (process_result) {
					this.tripsDataUnfiltered = process_result
					//this.tripsData = this.filterTripsForSelectedDate(process_result)
					this.tripsData = process_result
					this.rawData = process_result
					this.processing = false
					this.processTripsData('registerProcess')
					console.warn('data: registerProcess completed with result', this.tripsData)
				}
				// We have no results from ETL
				else {
					// Load and wait for saved days
					if (!this.savedDays.length) {
						await this.getSavedDays('registerProcess')
						// Set the journey days to the saved days
						this.journeyDays = this.savedDays
					}

					console.warn('data: registerProcess completed without result')

					if (this.isApp) {
						// Make sure we set the available dates from ETL for the datepicker
						this.setAvailableDatesFromETL()
					}
					setTimeout(() => {
						this.processing = false
						const appStore = useAppStore()
						// If current journey day is approved (from saved days)
						if (this.currentJourneyDay && this.currentJourneyDay.approved) {
							appStore.setPage('approved')
						} else {
							appStore.setPage('intro')
						}
						appStore.loaded = true
					}, 1000)
				}
			}

			this.processHistory.push(process_entry)
		},

		nextJourney(inc = 1) {
			console.warn('nextJourney', inc)
			// Exit edit mode
			const appStore = useAppStore()
			if (appStore.editStep != '') {
				appStore.editStep = ''
			}

			if (this.currentJourneyIndex + inc > this.currentJourneys.length - 1) {
				if (this.currentDayIndex < this.journeyDays.length - 1) {
					// this.currentDayIndex += 1
					// this.currentJourneyIndex = 0
					// this.selectedDate = new Date(this.journeyDays[this.currentDayIndex].date)
					// console.warn('next journey is next day', this.currentDayIndex, this.currentJourneyIndex, this.selectedDate)
				}
			} else {
				this.currentJourneyIndex = inc === 0 ? 0 : this.currentJourneyIndex + inc
			}

			const mapStore = useMapStore()
			mapStore.plotTrips()
		},

		storeDestinationPlace(journey) {
			if (!journey || !journey.destination || !journey.destination.place_id) return

			var clonedDestination = {
				place_id: journey.destination.place_id,
				type: journey.destination.type,
				title: journey.destination.title,
				subTitle: journey.destination.subTitle,
				categories: journey.destination.categories,
				latitude: journey.destination.latitude,
				longitude: journey.destination.longitude,
				placeTypes: journey.destination.placeTypes,
				formattedAddress: journey.destination.formattedAddress,
			}
			// Keep the associated survey responses for autocompletion
			clonedDestination.surveyResponses = cloneDeep(journey.surveyResponses)

			// Keep the destination place for later lookup - sanitize place_id to avoid JavaScript syntax errors
			const sanitizedPlaceId = this.sanitizePlaceId(journey.destination.place_id)
			this.places[sanitizedPlaceId] = clonedDestination

			// Store places
			kettle.set('places', JSON.stringify(this.places))
		},

		approveJourney() {
			if (!this.currentJourney) return

			const appStore = useAppStore()

			this.currentJourney.approved = true
			this.currentJourney.approved_timestamp = new Date().getTime()

			// Save current travel day
			this.saveCurrentTravelDay(true)

			// Update journey days approved status
			this.updateJourneyDaysApproved()

			// Hide sheet temporarily and show next journey
			appStore.bottomSheet.close(500, 'approveJourney')

			// Store the destination place for later lookup
			this.storeDestinationPlace(this.currentJourney)

			// Switch to the relevant page and journey
			setTimeout(() => {
				if (this.currentJourneyDay?.approved) {
					appStore.setPage('approved')

					// Save a flag indicating a journey has been approved
					this.hasApprovedJourney = true
					// Save the kettle state
					kettle.set('journey_approved', 'true')
				} else {
					this.clearTrip()
					appStore.setPage('trips')
				}
				// Show the sheet again after a short delay
				setTimeout(() => {
					//appStore.bottomSheet.open(800, 'approveTrip timeout')
					appStore.bottomSheet.autoResize(800, true, false, 'approveJourneyTimeout')
					//appStore.bottomSheet.setHeightNormal(800, 'approveTripTimeout')
				}, 500)
			}, 500)
		},

		clearTrip() {
			if (!this.currentJourney) return
			// this.currentJourney.selectedPurpose = null
			// this.currentJourney.selectedPoi = null
		},

		updateJourneyDayApproved(journeyDay) {
			journeyDay.approved = journeyDay.journeys.every((journey) => journey.approved)
			journeyDay.verified = journeyDay.journeys.every((journey) => journey.verified)
			if (journeyDay.approved) {
				journeyDay.approved_timestamp = journeyDay.journeys.reduce((acc, journey) => (journey.approved_timestamp > acc ? journey.approved_timestamp : acc), 0)
			}
			if (journeyDay.verified) {
				journeyDay.verified_timestamp = journeyDay.journeys.reduce((acc, journey) => (journey.verified_timestamp > acc ? journey.verified_timestamp : acc), 0)
			}
		},

		updateJourneyDaysApproved() {
			this.journeyDays.forEach((journeyDay) => {
				this.updateJourneyDayApproved(journeyDay)
			})

			const appStore = useAppStore()
			if (appStore.onboarded && this.currentJourneyDay?.approved) {
				appStore.setPage('approved')
			}
		},

		deleteProperties(item, keys) {
			keys.forEach((key) => delete item[key])
		},

		deleteEtlOriginDestinationMetrics(item) {
			const keysToDelete = ['nextLocationsDeltaSpeedAverageClass', 'first', 'originaltimestamp', 'activities', 'nearestActivity', 'nextActivity', 'nextAverageSpeedClass', 'nextCloseAverageSpeedClass', 'nextLocationsDeltaSpeedClass', 'nextLocationsSpeedClass', 'previousAverageSpeedClass', 'probableCurrentSpeedClass', 'vacc', 'debug', 'beacon', 'transport', 'pedometer', 'heading', 'clusterDistance', 'clusterIndex', 'clusterVisitIndex', 'course', 'floor', 'accumulatedDistance', 'angle', 'cacc', 'distanceFromStart', 'distanceHome', 'altitude', 'insideCluster', 'secondmeter', 'sacc', 'rowindex', 'last', 'ignore']
			this.deleteProperties(item, keysToDelete)
		},

		deleteEtlTripMetrics(item) {
			const keysToDelete = ['destination_pois', 'origin_pois', 'state', 'user_agent', 'transportRequestItem', 'selectedTransport', 'index_absolute', 'index_day', 'index_journey', 'index_relative', 'cleaned']
			this.deleteProperties(item, keysToDelete)
		},

		minifyJourney(item) {
			const keysToDelete = ['surveyJourneyCompleted', 'surveyTripsAutocompleted', 'surveyTripsCompleted', 'hasTripSurveys', 'deleteIndex']
			this.deleteProperties(item, keysToDelete)
			if (item.origin) this.deleteEtlOriginDestinationMetrics(item.origin)
			if (item.destination) this.deleteEtlOriginDestinationMetrics(item.destination)
			item.trips.forEach((trip) => {
				this.deleteEtlTripMetrics(trip)
				if (trip.origin) this.deleteEtlOriginDestinationMetrics(trip.origin)
				if (trip.destination) this.deleteEtlOriginDestinationMetrics(trip.destination)
				trip.locations = encodePolyline(trip.locations, 100)
			})
		},

		getMinifiedTravelDay() {
			// Clone and prepare data
			const clonedTravelDay = JSON.parse(JSON.stringify(this.currentJourneyDay))

			// Update approved status for the whole travel day
			this.updateJourneyDayApproved(clonedTravelDay)

			// Compress trip locations before saving
			clonedTravelDay.journeys.forEach((journey) => {
				this.minifyJourney(journey)
			})

			delete clonedTravelDay.autofill
			return clonedTravelDay
		},

		async saveCurrentTravelDay(approved) {
			// Always evaluate journey day with merges all survey reponses
			this.evaluateCurrentJourneyDaySurveyResponses()

			// Dont save if mock user
			if (this.respondentAltId == this.mockUserId) {
				console.warn('saveCurrentTravelDay: Mock user, skipping save')
				return
			}

			const appStore = useAppStore()
			var travelDay = this.getMinifiedTravelDay()

			// Add some additional metadata
			travelDay.metrics = {
				consentsGrantedDate: this.consentsGrantedDate,
				installDate: this.installDate.toISOString(),
				selectedDate: this.selectedDate.toISOString(),
				etlVersion: Etl.DEFAULTS.etlVersion,
				etlDate: Etl.DEFAULTS.etlDate,
				appVersion: appStore.version,
				appDate: appStore.version_date,
				appState: this.appState,
				platform: this.platform,
				userAgent: this.appUserAgent,
			}

			travelDay.survey = {
				startedDate: this.surveyStartedDate,
				completedDate: this.surveyCompletedDate,
				id: this.surveyId,
				shortId: this.surveyShortId,
			}

			travelDay.respondent = {
				id: this.respondentId,
				shortId: this.respondentShortId,
				altId: this.respondentAltId,
			}

			travelDay.permissions = {
				startedDate: this.permissionStartedDate,
				completedDate: this.permissionCompletedDate,
			}

			console.warn('dataStore: saveCurrentTravelDay', travelDay)

			// Don't save if diary is disabled
			if (!this.disableDiary && !this.disableSaving) {
				await dataService.createTravelDay(this.appInstallationId, travelDay)
				// Track the save event when journey is approved
				if (approved) appStore.events.trackTravelDay(travelDay)

				// Track special event if whole travel day is approved (all journeys are approved)
				if (travelDay.approved) appStore.events.trackTravelDayCompleted(travelDay)

				// Refresh saved days
				await this.getSavedDays('saveCurrentTravelDay')
			} else {
				console.warn('dataStore: saveCurrentTravelDay - Diary is disabled')
			}
		},

		async getSavedDays(source) {
			console.warn('data: fetching saved days -', source)

			// If repondentAltId is mockUserId, don't fetch saved days
			if (this.respondentAltId == this.mockUserId) {
				console.warn('getSavedDays: Mock user, skipping fetch')
				this.savedDays = []
				return
			}

			//const travelDaysData = await window.etl.getTravelDays()
			if (this.disableDiary) {
				console.warn('getSavedDays: Diary is disabled')
				return
			}

			const travelDaysData = await dataService.getTravelDays(this.appInstallationId)
			console.warn('travelDaysData', JSON.parse(travelDaysData[0]?.data ?? '{}'))

			if (!travelDaysData || travelDaysData.length === 0) {
				this.savedDays = []
				return
			}
			this.savedDays = travelDaysData.map((day) => {
				try {
					const travelDay = JSON.parse(day.data)
					console.warn('travelDay', travelDay)
					// Decode polylines back to locations
					if (travelDay.journeys) {
						travelDay.journeys.forEach((journey) => {
							journey.trips.forEach((trip) => {
								trip.locations = decodePolyline(trip.locations)
							})
						})
					}
					travelDay.id = day.id
					return travelDay
				} catch (error) {
					console.error('getSavedDays', error)
					return {
						id: day.id,
					}
				}
			})

			console.warn('data: processed saved days', this.savedDays)

			// Create a lookup object for saved days
			this.savedDaysLookup = this.savedDays.reduce((acc, day) => {
				acc[day.date] = day
				return acc
			}, {})

			console.warn('getSavedDays', cloneDeep(this.savedDays))

			return this.savedDays
		},

		mergeSavedDays() {
			if (this.journeyDays && this.journeyDays.length > 0) {
				this.savedDays.forEach((savedDay) => {
					this.journeyDays.forEach((journeyDay) => {
						if (savedDay.date == journeyDay.date) {
							if (savedDay.journeys.length == journeyDay.journeys.length) {
								// Iterate savedDay journeys with for loop
								for (let i = 0; i < savedDay.journeys.length; i++) {
									let savedJourney = savedDay.journeys[i]
									if (savedJourney.approved) {
										// Replace journeyDay journey with savedJourney
										journeyDay.journeys[i] = savedJourney
										console.warn('Merged saved journey', savedJourney)
									}
								}
								journeyDay.surveyResponses = savedDay.surveyResponses ?? []
								journeyDay.surveyDayCompleted = savedDay.surveyDayCompleted ? savedDay.surveyDayCompleted : false
								journeyDay.surveyJourneysCompleted = savedDay.surveyJourneysCompleted ? savedDay.surveyJourneysCompleted : false
							} else {
								// Replace all journeys since the number of journeys are different
								journeyDay.journeys = savedDay.journeys
							}
						}
					})
				})
			} else {
				// If etl has now generated journeys but we have saved days, the user has probably added them manually so we need to add them
				this.journeyDays = cloneDeep(this.savedDays)
			}

			// Also add saved days which does not exist in journeyDays
			this.savedDays.forEach((savedDay) => {
				if (!this.journeyDays.find((journeyDay) => journeyDay.date === savedDay.date)) {
					this.journeyDays.push(savedDay)
					console.warn('Added saved day to journey days which does not exist', savedDay)
				}
			})

			// Change currentJourneyIndex back and forth to trigger reactivity
			this.currentJourneyIndex++
			this.currentJourneyIndex--

			// Update approved and verified status for each day
			this.updateJourneyDaysApproved()
		},

		clearJourneyDayApproval() {
			console.warn('clearJourneyDayApproval')
			this.currentJourneyDay.approved = false
			this.currentJourneyIndex = 0
			const appStore = useAppStore()
			appStore.setPage('trips')

			const dateStr = `${this.selectedDate.getUTCFullYear()}-${String(this.selectedDate.getUTCMonth() + 1).padStart(2, '0')}-${String(this.selectedDate.getUTCDate()).padStart(2, '0')}`
			const travelDayId = `${this.appInstallationId}+${dateStr}`
			if (!this.disableDiary && !this.disableSaving) {
				dataService.deleteTravelDayById(travelDayId, this.appInstallationId)
			}
		},

		async deleteAllJourneyDays() {
			if (!window.etl) {
				// TODO: Handle this
				window.etl = new Etl(false)
				window.etl.appInstallationId = this.appInstallationId
			}

			// Get saved days
			await this.getSavedDays('deleteAllJourneyDays')

			// Delete each saved day
			for (const day of this.savedDays) {
				if (!this.disableDiary && !this.disableSaving) {
					await dataService.deleteTravelDayById(day.id, this.appInstallationId)
				}
			}

			// Reset state
			this.savedDays = []
			this.journeyDays = this.originalJourneyDays
		},

		editTrip(trip, index) {
			console.warn('dataStore: editTrip', trip)
			let tripCopy = JSON.parse(JSON.stringify(trip))
			let tripCopyOriginal = JSON.parse(JSON.stringify(trip))
			this.extendTripDates(tripCopyOriginal)
			this.extendTripDates(tripCopy)
			this.currentTripIndex = index ?? trip.index
			this.currentEditOriginal = tripCopyOriginal
			this.currentEditTrip = tripCopy
			this.currentTrip = trip
		},

		async addRouteDataForEditedTrip() {
			await this.addRouteDataForTrip(this.currentEditTrip)
			if (this.currentEditTrip && this.currentEditTrip.locations) {
				const mapStore = useMapStore()
				mapStore.plotCurrentEdit()
				return this.currentEditTrip
			}
		},

		depoly(data) {
			return decodePolyline(data)
		},

		async addRouteDataForTrip(trip) {
			if (trip?.origin?.latitude && trip?.destination?.latitude) {
				let directions = await geoService.getDirectionsForTrip(trip)

				if (directions && directions.routes && directions.routes.length > 0) {
					let route = directions.routes[0]
					let firstLeg = route.legs[0]
					let routeDuration = firstLeg.duration.value
					let routeDistance = firstLeg.distance.value

					// If the trip is the start of a new journey, adjust the departure timestamp to subtract the route duration
					if (dataStore.currentEditTrip?.newjourney && dataStore.currentEditTrip?.orderjourney < 0) {
						// Set the departure timestamp by subtracting the route duration
						trip.departure_timestamp = trip.arrival_timestamp - routeDuration * 1000
					} else {
						// Update arrival timestamp based on route duration
						trip.arrival_timestamp = trip.departure_timestamp + routeDuration * 1000
					}

					this.extendTripDates(trip)
					// Add locations from route
					trip.locations = decodePolyline(route.overview_polyline)
					// Calculate seconds per location using routeDuration
					const secondsPerLocation = routeDuration / trip.locations.length
					trip.locations.forEach((location, index) => {
						location.source = 'route'
						// Calculate evenly distributed timestamps for each location
						location.timestamp = trip.departure_timestamp + index * secondsPerLocation * 1000
					})
					trip.distance = routeDistance
					trip.duration = routeDuration
					return directions
				}
			}
		},

		addJourney(orderjourney, stationaryDay = false) {
			this.currentEditTrip = this.defaultNewTrip
			this.currentEditTrip.activity_name = stationaryDay ? 'stationary' : 'automotive'
			this.currentEditTrip.newjourney = true
			this.currentEditTrip.orderjourney = orderjourney ?? 0
			this.currentEditTrip.new = true
			this.currentEditTrip.stationary = stationaryDay ?? false

			var defaultTime = new Date(this.selectedDate)
			defaultTime.setHours(12, 0, 0, 0)
			const defaultTimestamp = defaultTime.getTime()

			if (this.currentEditTrip.orderjourney < 0) {
				// Set the arrival timestamp to be the departue timestamp of the journey we are before
				this.currentEditTrip.arrival_timestamp = this.currentFirstTrip?.departure_timestamp ?? defaultTimestamp
				this.currentEditTrip.departure_timestamp = this.currentEditTrip.arrival_timestamp
			} else {
				// Set the departure timestamp to be the arrival timestamp of the journey we are after
				this.currentEditTrip.departure_timestamp = this.currentLastTrip?.arrival_timestamp ?? defaultTimestamp
				this.currentEditTrip.arrival_timestamp = this.currentEditTrip.departure_timestamp
			}
			this.extendTripDates(this.currentEditTrip)
		},

		addTrip() {
			let addIndex = this.currentJourney.addIndex - 1
			this.currentEditTrip = this.defaultNewTrip
			this.currentEditTrip.activity_name = 'automotive'

			let selectedTrip = addIndex < this.currentJourney.trips.length ? this.currentJourney.trips[addIndex] : null
			if (this.currentJourney.addIndex == 0) selectedTrip = this.currentJourney.trips[this.currentJourney.addIndex]

			if (selectedTrip) {
				this.currentEditTrip.departure_timestamp = this.currentJourney.addIndex == 0 ? selectedTrip.departure_timestamp : selectedTrip.arrival_timestamp
				this.currentEditTrip.arrival_timestamp = this.currentJourney.addIndex == 0 ? selectedTrip.departure_timestamp : selectedTrip.arrival_timestamp
				this.currentEditTrip.new = true
				this.extendTripDates(this.currentEditTrip)
			}
		},

		closeEditTripDialogClick() {
			this.showEditTripDialog = false
			this.currentEditTrip = null
			this.currentTrip = null
		},

		closeLoginDialogClick() {
			this.showLoginDialog = false
			console.warn('dataStore: closeLoginDialogClick')
		},

		deleteTripWithIndex(index) {
			// Mark trip as deleted
			this.currentJourney.trips[index].deleted = this.currentJourney.trips[index].deleted ? false : true
		},

		recalculateJourneyEndpoints() {
			const trips = this.currentJourney.trips
			const nonDeletedTrips = trips.filter((trip) => !trip.deleted)

			// If there are no non-deleted trips, nothing to do
			if (nonDeletedTrips.length === 0) return

			// Set journey origin to the origin of the first non-deleted trip
			this.currentJourney.origin = nonDeletedTrips[0].origin

			// Set journey destination to the destination of the last non-deleted trip
			this.currentJourney.destination = nonDeletedTrips[nonDeletedTrips.length - 1].destination
		},

		deleteTrip(tripToDelete) {
			if (!tripToDelete) return

			// Toggle delete status
			if (tripToDelete.deleted) {
				tripToDelete.deleted = false
				delete tripToDelete.deleted_timestamp
				// Show toast message
				appStore.toast({ message: 'Ettappen er nå ikke lenger merket som slettet', type: 'success', duration: 5000 })
			} else {
				// If trips was created by user it can be removed completely
				if (tripToDelete.created) {
					this.currentJourney.trips = this.currentJourney.trips.filter((trip) => trip.id !== tripToDelete.id)
					// Show toast message
					appStore.toast({ message: 'Etappe ble slettet', type: 'success', duration: 5000 })
				} else {
					// Mark the trip as deleted
					tripToDelete.deleted = true
					tripToDelete.deleted_timestamp = new Date().getTime()
					tripToDelete.edited = true
					tripToDelete.edit_timestamp = new Date().getTime()
					// Show toast message
					appStore.toast({ message: 'Denne etappen er nå markert som slettet', duration: 5000, icon: h('i', { class: 'fa-solid fa-trash', style: 'color: var(--color-danger); font-size:1.125rem;' }) })
				}
			}

			// Recalculate journey endpoints in both cases
			this.recalculateJourneyEndpoints()

			// Remove previous survey answers
			clearSurveyAnswers(this.currentJourney.survey)

			// Make sure the final journey data questions are updated since we have deleted a trip (maybe we dont have any automotive trips left)
			this.addSurveyToCurrentJourneyDay()

			// Update survey for the current journey and its trips
			this.addSurveyToCurrentJourney()

			this.currentEditTrip = null
		},

		deleteCurrentJourney() {
			// Store a copy of the journey before deleting it
			const journeyToDelete = JSON.parse(JSON.stringify(this.currentJourneyDay.journeys[this.currentJourneyIndex]))

			// Initialize deletedJourneys array if it doesn't exist
			if (!this.currentJourneyDay.deletedJourneys) this.currentJourneyDay.deletedJourneys = []

			// Store a deep copy of the deleted journey
			this.currentJourneyDay.deletedJourneys.push(journeyToDelete)

			// Remove the journey from the journeys array
			this.currentJourneyDay.journeys.splice(this.currentJourneyIndex, 1)

			// Change the currentJourneyIndex to the previous journey
			if (this.currentJourneyIndex > 0) this.currentJourneyIndex--

			// Replot the trips
			mapStore.plotTrips()

			// Update journeyDay surveys in case we dont have any more automotive trips
			this.addSurveyToCurrentJourneyDay()
		},

		updateTimeStampsFromTimes(trip, forSaving = false) {
			console.warn('data: updateTimeStampsFromTimes', trip.new ? 'new' : 'existing', forSaving, trip)

			if (!trip.startDate || !trip.endDate) {
				this.extendTripDates(trip)
			}

			var startDate = new Date(trip.startDate)
			var endDate = new Date(trip.endDate)

			const newDepartureDateStr = startDate.toShortDateString()
			const newDepartureTimestamp = new Date(newDepartureDateStr + 'T' + trip.startTime).getTime()
			const newArrivalDateStr = endDate.toShortDateString()
			const newArrivalTimestamp = new Date(newArrivalDateStr + 'T' + trip.endTime).getTime()

			// Keep history of original timestamp if not set
			// Time can only be edited in hours and minutes, so we need to check if the timestamp has changed more than one minute
			if (!trip.new && forSaving) {
				if (!trip.original_departure_timestamp && Math.abs(trip.departure_timestamp - newDepartureTimestamp) >= 60000) {
					trip.original_departure_timestamp = trip.departure_timestamp
				}
				if (!trip.original_arrival_timestamp && Math.abs(trip.arrival_timestamp - newArrivalTimestamp) >= 60000) {
					trip.original_arrival_timestamp = trip.arrival_timestamp
				}
			}

			trip.arrival_timestamp = newArrivalTimestamp
			trip.destination.timestamp = newArrivalTimestamp
			trip.origin.timestamp = newDepartureTimestamp
			trip.departure_timestamp = newDepartureTimestamp
		},

		saveNewJourney() {
			console.warn('data: saveNewJourney', cloneDeep(this.currentEditTrip))
			if (!this.currentEditTrip) return

			this.currentEditTrip.id = 1
			this.currentEditTrip.index = 1
			var newTrip = cloneDeep(this.currentEditTrip)

			// Calculate duration and for new trip in new journey
			newTrip.duration = (newTrip.arrival_timestamp - newTrip.departure_timestamp) / 1000

			// Calculate distances using locations
			var distances = calculateDistances(newTrip.locations)
			newTrip.distance = distances.total_distance
			newTrip.air_distance = distances.air_distance
			newTrip.max_distance = distances.max_distance
			newTrip.created = true

			// New journey object
			var newJourney = {
				name: 0,
				date: dataStore.selectedDate.toShortDateString(),
				origin: newTrip.origin,
				destination: newTrip.destination,
				trips: [newTrip],
				created_timestamp: new Date().getTime(),
				created: true,
			}

			// Mark the new journey as stationary
			if (this.currentEditTrip.stationary) {
				newJourney.stationary = true
			}

			// Index of current journey
			var journeyIndex = dataStore.currentJourneyIndex

			// If this.currentEditTrip.orderjourney is below 0 then we need to add the new journey before, if its above 0 we need to add it after
			if (this.currentEditTrip.orderjourney < 0) {
				// Add new journey before the current journey
				journeyIndex = Math.max(0, journeyIndex - 1)
			}

			if (this.currentEditTrip.orderjourney > 0) {
				// Add new journey after the current journey
				journeyIndex = Math.min(this.currentJourneyDay.journeys.length, journeyIndex + 1)
			}

			// Add new journey to current day
			this.currentJourneyDay.journeys.splice(journeyIndex, 0, newJourney)

			// Add surveys to new journey
			this.resetSurveysFromJourneyIndex(journeyIndex)

			// Clear after adding new journey
			this.currentEditTrip = null

			// Show the new journey
			this.currentJourneyIndex = journeyIndex

			// Special handling of stationary day checking dataStore.currentEditTrip.stationary
			if (newJourney.stationary) {
				// Lets just approve the journey directly
				dataStore.approveJourney()
			}
		},

		saveTrip() {
			console.warn('data: saveTrip', cloneDeep(this.currentEditTrip))
			if (!this.currentEditTrip) return

			if (this.currentEditTrip.new && this.currentJourney.addIndex > -1) {
				// Find highest existing trip id
				var highestId = 0
				this.currentJourney.trips.forEach((trip) => {
					if (trip.id > highestId) highestId = trip.id
				})
				this.currentEditTrip.id = highestId + 1
				this.currentEditTrip.index = this.currentEditTrip.id

				// Calculate duration if missing or zero
				if (!this.currentEditTrip.duration || this.currentEditTrip.duration == 0) {
					this.currentEditTrip.duration = (this.currentEditTrip.arrival_timestamp - this.currentEditTrip.departure_timestamp) / 1000
				}

				// Calculate distances using locations
				var distances = calculateDistances(this.currentEditTrip.locations)

				// Add distances if missing or zero
				if (!this.currentEditTrip.distance || this.currentEditTrip.distance == 0) this.currentEditTrip.distance = distances.total_distance
				if (!this.currentEditTrip.air_distance || this.currentEditTrip.air_distance == 0) this.currentEditTrip.air_distance = distances.air_distance
				if (!this.currentEditTrip.max_distance || this.currentEditTrip.max_distance == 0) this.currentEditTrip.max_distance = distances.max_distance

				this.currentEditTrip.new = false

				// Add new trip to current journey
				this.currentJourney.trips.splice(this.currentJourney.addIndex, 0, cloneDeep(this.currentEditTrip))

				// Clear after adding new trip
				this.currentJourney.addIndex = -1
				this.currentEditTrip = null
			} else {
				if (this.currentTrip) {
					// Store original values if changed and not already stored
					if (!this.currentTrip.original_activity_name && this.currentEditTrip.activity_name != this.currentTrip.activity_name) {
						this.currentTrip.original_activity_name = this.currentTrip.activity_name
					}

					// Set new timestamps based on current date and time inputs
					this.updateTimeStampsFromTimes(this.currentEditTrip, true)

					// Update trip with pt values
					if (this.currentEditTrip.pt) {
						this.currentTrip.pt = this.currentEditTrip.pt
					}

					// Update trip with new values
					this.currentTrip.activity_name = this.currentEditTrip.activity_name
					this.currentTrip.comment = this.currentEditTrip.comment
					this.currentTrip.origin = this.currentEditTrip.origin
					this.currentTrip.destination = this.currentEditTrip.destination
					this.currentTrip.locations = this.currentEditTrip.locations
					this.currentTrip.edit_timestamp = new Date().getTime()
					this.currentTrip.edits = this.currentEditTrip.edits

					// Lets keep information about trip creation
					if (!this.currentTrip.created && this.currentEditTrip.created) {
						this.currentTrip.created_timestamp = new Date().getTime()
						this.currentTrip.created = true
					}

					// Update timestamps
					this.currentTrip.arrival_timestamp = this.currentEditTrip.arrival_timestamp
					this.currentTrip.departure_timestamp = this.currentEditTrip.departure_timestamp
					if (this.currentEditTrip.original_departure_timestamp) this.currentTrip.original_departure_timestamp = this.currentEditTrip.original_departure_timestamp
					if (this.currentEditTrip.original_arrival_timestamp) this.currentTrip.original_arrival_timestamp = this.currentEditTrip.original_arrival_timestamp

					this.currentTrip.edited = true

					// Reset edit trip
					this.currentEditTrip = null
				}
			}
			// Update journey origin and destination from trips
			this.currentJourney.origin = this.currentJourney.trips[0].origin
			this.currentJourney.destination = this.currentJourney.trips[this.currentJourney.trips.length - 1].destination

			// Make sure journey day survey reflects the current journey
			this.addSurveyToCurrentJourneyDay()

			// Update survey for current journey
			this.addSurveyToCurrentJourney()
		},

		extendTripDates(trip) {
			trip.edit_timestamp = new Date().getTime()
			trip.startDate = new Date(trip.departure_timestamp)
			trip.startDateString = trip.startDate.toShortDateString()
			trip.original_startTime = trip.startDate
			trip.endDate = new Date(trip.arrival_timestamp)
			trip.endDateString = trip.endDate.toShortDateString()
			trip.original_endTime = trip.endDate
			trip.startTime = this.formatTime(trip.startDate)
			trip.endTime = this.formatTime(trip.endDate)
			//trip.duration = this.formatDuration(trip.startDate, trip.endDate)
		},

		getTripsForJoin(includeNextJourneyTrips = false) {
			const appStore = useAppStore()
			if (appStore.editStep == 'joinPrevJourney') {
				return dataStore.currentJourneys[dataStore.currentJourneyIndex - 1].trips.concat(dataStore.currentJourney.trips)
			} else if (appStore.editStep == 'joinNextJourney' || (includeNextJourneyTrips && dataStore.currentJourneyIndex < dataStore.currentJourneys.length - 1)) {
				return dataStore.currentJourney.trips.concat(dataStore.currentJourneys[dataStore.currentJourneyIndex + 1].trips)
			} else {
				return dataStore.currentJourney.trips
			}
		},

		async clearTrips() {
			// Sletter lagrede reiser
			await this.deleteAllJourneyDays()
			console.warn('JourneyDays cleared')
		},

		async clearAll() {
			// Sletter alle IndexedDB databaser
			const dbs = await window.indexedDB.databases()
			dbs.forEach((db) => {
				window.indexedDB.deleteDatabase(db.name)
			})
			console.warn('IndexedDB cleared')

			// Sletter alt fra localStorage, sessionStorage og cookies
			clearStates()

			// Sletter lagrede reiser
			await clearTrips()
		},

		clearUser() {
			const appStore = useAppStore()
			appStore.surveyCompleted = false

			// Sletter survey relatert
			localStorage.removeItem('userCanChangeDates')
			localStorage.removeItem('userCanNavigateTrips')
			localStorage.removeItem('userCanUndo')
			localStorage.removeItem('survey_definitions')

			kettle.remove('onboarded_survey')
			kettle.remove('respondent_id')
			kettle.remove('respondent_short_id')
			kettle.remove('respondent_alt_id')
			kettle.remove('survey_completed')
			kettle.remove('survey_completed_date')
			kettle.remove('survey_started_date')
			kettle.remove('permission_completed_date')
			kettle.remove('permission_started_date')

			this.respondentId = ''
			this.respondentShortId = ''
			this.respondentAltId = ''

			// Sletter alt fra sessionStorage
			sessionStorage.clear()

			// Sletter alle cookies for domenet
			var cookies = document.cookie.split(';')
			for (var i = 0; i < cookies.length; i++) {
				var cookie = cookies[i]
				var eqPos = cookie.indexOf('=')
				var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie
				document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'
			}
		},

		clearPermissions() {
			localStorage.removeItem('onboarded_permissions')
			localStorage.removeItem('location_always')
			localStorage.removeItem('bluetooth')
			localStorage.removeItem('location_wheninuse')
			localStorage.removeItem('motion')
			localStorage.removeItem('notification')
			localStorage.removeItem('userCanChangeDates')
			localStorage.removeItem('userCanNavigateTrips')
			localStorage.removeItem('userCanUndo')
		},

		resetSurvey() {
			const appStore = useAppStore()
			appStore.surveyCompleted = false
			this.deleteSurvey()
			kettle.remove('onboarded_survey')
			kettle.remove('respondent_id')
			kettle.remove('respondent_short_id')
			kettle.remove('respondent_alt_id')
			kettle.remove('survey_completed')
			localStorage.removeItem('survey_definitions')
			this.respondentId = ''
			this.respondentShortId = ''
			this.respondentAltId = ''
		},

		clearStates() {
			// Sletter alt fra localStorage
			localStorage.clear()
			console.warn('localStorage cleared')

			// Sletter alt fra sessionStorage
			sessionStorage.clear()
			console.warn('sessionStorage cleared')

			// Sletter alle cookies for domenet
			var cookies = document.cookie.split(';')

			for (var i = 0; i < cookies.length; i++) {
				var cookie = cookies[i]
				var eqPos = cookie.indexOf('=')
				var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie
				document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'
			}
			console.warn('cookies cleared')
		},

		sanitizePlaceId(placeId) {
			if (!placeId) return 'unknown'
			// Replace problematic characters that could cause JavaScript syntax errors
			// when used as object keys, especially Norwegian characters with apostrophes
			return placeId
				.replace(/['"]/g, '_') // Replace quotes and apostrophes
				.replace(/[^\w\-_.]/g, '_') // Replace any non-alphanumeric characters except dash, underscore, dot
				.replace(/_+/g, '_') // Replace multiple underscores with single underscore
				.trim()
		},
	},
})
